import React, {
  useState,
  useEffect,
  ChangeEvent,
  useContext,
  useRef,
  Fragment,
} from "react";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { DesignContext } from "../../context/design";
import {
  paperList,
  scaleList,
  scaleBars,
  mmToPixel,
} from "../../constant/commons";
import "mapbox-gl/dist/mapbox-gl.css";
import clsx from "clsx";
import { MainContext } from "../../context/main";
import { LangContext } from "../../context/lang";
import Preview from "./preview";
import GenPdf from "./gen-load-pdf";

const useStyles = makeStyles((theme: Theme) => ({
  container: { paddingRight: theme.spacing(1) },
  childContainer: { paddingLeft: theme.spacing(4) },
  actionDiv: { display: "flex", justifyContent: "space-between" },
  actionBtn: { textTransform: "unset" },
  desTable: {
    position: "fixed",
    zIndex: -1,
    borderStyle: "solid",
    borderWidth: 1,
  },
  scaleBar: {
    position: "fixed",
    zIndex: -1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "transparent",
  },
  headerDesTable: {
    display: "flex",
    justifyContent: "center",
  },
  contentTextDesTable: { fontWeight: 400 },
  rightBounder: {
    borderWidth: 1,
    borderColor: "black",
    borderRightStyle: "solid",
  },
  bottomBorder: {
    borderWidth: 1,
    borderColor: "black",
    borderBottomStyle: "solid",
    padding: theme.spacing(2),
  },
  centerContent: { textAlign: "center" },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    // color: '#fff',
  },
}));

const PrintDesign = () => {
  const classes = useStyles();
  const desTable = useRef<HTMLDivElement | any>(null);
  const scaleBar = useRef<HTMLDivElement | any>(null);
  const { state } = useLocation();
  const [loading, setloading] = useState<boolean>(false);
  const [preview, setpreview] = useState<boolean>(false);
  const [dpi, setdpi] = useState<32 | 300>(32);
  const [geningPdf, setgeningPdf] = useState<boolean>(false);

  const { userInfo } = useContext(MainContext);
  const { lang } = useContext(LangContext);
  const { map, event } = useContext(DesignContext);
  const { designEquips, anno } = event;

  const [printparam, setprintparam] = useState<any>({
    paperSize: "a3",
    layout: "landscape",
    scale: 18.384615384615,
    grayscale: true,
  });

  useEffect(() => {
    const jsonState = typeof state === "string" ? JSON.parse(state) : state;
    const {
      customer_address,
      customer_name,
      department_name,
      detail,
      work_order_no,
    } = jsonState;
    setprintparam({
      customer_address,
      customer_name,
      department_name,
      detail,
      work_order_no,
      paperSize: "a3",
      layout: "landscape",
      scale: 18.384615384615,
      compass: true,
      overview: true,
      grayscale: true,
    });
    return () => {};
  }, [state]);

  const detailList = [
    { name: "work_order_no", label: lang.data.workOrderNo },
    { name: "department_name", label: lang.data.department },
    { name: "customer_name", label: lang.data.customerName },
    { name: "customer_address", label: lang.data.customerAddress },
    { name: "detail", label: lang.data.detail },
    { name: "estimator", label: lang.data.estimator },
    { name: "examiner", label: lang.data.examiner },
    { name: "head", label: lang.data.head },
    { name: "division_director", label: lang.data.divisionDirector },
    { name: "district_director", label: lang.data.districtDirector },
  ];

  const _changeSelect = (name: string) => (
    e: ChangeEvent<{ value: unknown }>
  ) => {
    if (name === "scale") {
      map?.zoomTo(Number(e.target.value));
    }
    setprintparam({ ...printparam, [name]: e.target.value });
  };

  const _changeInputElement = (event: ChangeEvent<HTMLInputElement>) => {
    setprintparam({ ...printparam, [event.target.name]: event.target.value });
  };

  const _changeCheck = (event: ChangeEvent<HTMLInputElement>) => {
    setprintparam({ ...printparam, [event.target.name]: event.target.checked });
  };

  const genPdf = () => {
    setgeningPdf(true);
    setloading(true);
    setdpi(300);
    GenPdf(
      map,
      printparam,
      designEquips,
      anno,
      scaleBar,
      desTable,
      setgeningPdf,
      setloading
    );
  };

  return (
    <div className={classes.container}>
      <Backdrop className={classes.backdrop} open={geningPdf}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Preview
        map={map}
        designEquips={designEquips}
        anno={anno}
        scaleBar={scaleBar}
        desTable={desTable}
        open={Boolean(preview)}
        onClose={() => {
          setpreview(false);
        }}
        printparam={printparam}
        setloading={setloading}
        genPdf={genPdf}
      />
      {loading && (
        <Fragment>
          <Grid
            ref={desTable}
            container
            className={classes.desTable}
            style={{ width: mmToPixel(170, dpi) }}
          >
            <Grid
              item
              xs={12}
              className={classes.headerDesTable}
              style={{
                padding: mmToPixel(2.5, dpi),
                borderWidth: 1,
                borderColor: "black",
                borderBottomStyle: "solid",
              }}
            >
              <Typography variant="h3" style={{ fontSize: mmToPixel(5, dpi) }}>
                {printparam.department_name}
              </Typography>
            </Grid>
            <Grid item xs={4} className={clsx(classes.rightBounder)}>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {userInfo?.mea ? `กอง ${userInfo.mea.div_short_name}` : `กอง`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`ผู้ทำแบบ `}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`ผู้ประมาณการ ${
                    printparam.estimator ? printparam.estimator : ""
                  }`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`ผู้ตรวจ ${printparam.examiner ? printparam.examiner : ""}`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`หัวหน้าแผนก ${printparam.head ? printparam.head : ""}`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`ผู้อำนวยการกอง ${
                    printparam.division_director
                      ? printparam.division_director
                      : ""
                  }`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi), borderWidth: 0 }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`ผู้อำนวยการฝ่าย/เขต ${
                    printparam.district_director
                      ? printparam.district_director
                      : ""
                  }`}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              item
              xs={4}
              className={clsx(classes.rightBounder)}
              style={{ padding: mmToPixel(2, dpi) }}
            >
              <Grid item xs={12}>
                <Typography
                  className={clsx(
                    classes.centerContent,
                    classes.contentTextDesTable
                  )}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`เลขรับที่ ${printparam.work_order_no}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  className={clsx(
                    classes.centerContent,
                    classes.contentTextDesTable
                  )}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {printparam.customer_name}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  className={clsx(
                    classes.centerContent,
                    classes.contentTextDesTable
                  )}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {printparam.customer_address}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={4}>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {userInfo?.mea
                    ? `แผนก ${userInfo.mea.sec_short_name}`
                    : `แผนก `}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`มาตราส่วน ${
                    scaleList.find((scale) => scale.zoom === printparam.scale)
                      ?.scale
                  }`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`วันที่ ${new Date().toLocaleDateString("th-TH", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`ระวางแผนที่หมายเลข \v\v\v\v\v\v\v\v\v\v\v\v\v\v โซน`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi) }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`แผ่นที่ \v\v\v\v\v\v\v\v\v\v\v\v\v\v ของจำนวน`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className={classes.bottomBorder}
                style={{ padding: mmToPixel(2, dpi), borderWidth: 0 }}
              >
                <Typography
                  className={classes.contentTextDesTable}
                  style={{ fontSize: mmToPixel(3, dpi) }}
                >
                  {`แบบเลขที่ ${printparam.work_order_no}`}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid ref={scaleBar} container className={classes.scaleBar}>
            <Grid
              item
              style={{
                display: "flex",
              }}
            >
              {scaleBars[`${printparam.scale}`].meters.map(
                (scaleGrid: number, i: number) => {
                  return (
                    <Grid
                      item
                      key={scaleGrid}
                      style={{
                        width: scaleBars[`${printparam.scale}`].widthPerGrid(
                          dpi
                        ),
                      }}
                    >
                      <Typography
                        variant="h5"
                        style={{
                          width: 16,
                          marginBottom: mmToPixel(1, dpi),
                          fontSize: mmToPixel(3, dpi),
                        }}
                      >
                        {scaleGrid}
                      </Typography>
                      {i <
                        scaleBars[`${printparam.scale}`].meters.length - 1 && (
                        <Fragment>
                          <div
                            style={{
                              borderStyle: "solid",
                              borderWidth: 1,
                              width: scaleBars[
                                `${printparam.scale}`
                              ].widthPerGrid(dpi),
                              height: mmToPixel(1.5, dpi),
                              background: Boolean((i + 1) % 2 !== 1)
                                ? "#ffffff"
                                : "#000000",
                            }}
                          />
                          <div
                            style={{
                              borderStyle: "solid",
                              borderWidth: 1,
                              width: scaleBars[
                                `${printparam.scale}`
                              ].widthPerGrid(dpi),
                              height: mmToPixel(1.5, dpi),
                              background: Boolean((i + 1) % 2 === 1)
                                ? "#ffffff"
                                : "#000000",
                            }}
                          />
                        </Fragment>
                      )}
                    </Grid>
                  );
                }
              )}
            </Grid>
            <Grid
              item
              style={{
                textAlign: "center",
                width:
                  scaleBars[`${printparam.scale}`].widthPerGrid *
                  (scaleBars[`${printparam.scale}`].meters.length - 1),
              }}
            >
              <Typography
                variant="h5"
                style={{ marginTop: 24, fontSize: mmToPixel(3, dpi) }}
              >
                เมตร
              </Typography>
            </Grid>
          </Grid>
        </Fragment>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">{lang.data.layout}</Typography>
          <Select
            variant="outlined"
            margin="dense"
            fullWidth
            value={printparam.layout}
            onChange={_changeSelect("layout")}
          >
            <MenuItem value={"landscape"}>{lang.data.landscape}</MenuItem>
            <MenuItem value={"portrait"}>{lang.data.portrait}</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">{lang.data.paperSize}</Typography>
          <Select
            variant="outlined"
            margin="dense"
            fullWidth
            value={printparam.paperSize}
            onChange={_changeSelect("paperSize")}
          >
            {paperList.map((paper) => (
              <MenuItem key={paper.id} value={paper.id}>
                {paper.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">{lang.data.scale}</Typography>
          <Select
            variant="outlined"
            margin="dense"
            fullWidth
            value={printparam.scale}
            onChange={_changeSelect("scale")}
          >
            {scaleList.map((scale) => (
              <MenuItem key={scale.zoom} value={scale.zoom}>
                {scale.scale}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">{lang.data.detail}</Typography>
          <div className={classes.childContainer}>
            <Grid container spacing={1}>
              {detailList.map(
                (detail: { name: string; label: string }, index: number) => {
                  return (
                    <Grid item xs={12} key={detail.name}>
                      <TextField
                        variant="outlined"
                        margin="dense"
                        label={detail.label}
                        fullWidth
                        disabled={Boolean(index < 5)}
                        multiline
                        rows={
                          Boolean(
                            detail.name === "detail" ||
                              detail.name === "customer_address"
                          )
                            ? 5
                            : 1
                        }
                        value={
                          printparam[detail.name] ? printparam[detail.name] : ""
                        }
                        onChange={_changeInputElement}
                        name={detail.name}
                      />
                    </Grid>
                  );
                }
              )}
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">{lang.data.other}</Typography>
          <FormControl component="fieldset" className={classes.childContainer}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(printparam.compass)}
                    onChange={_changeCheck}
                    color="primary"
                    name="compass"
                  />
                }
                label={lang.data.compass}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(printparam.overview)}
                    onChange={_changeCheck}
                    color="primary"
                    name="overview"
                  />
                }
                label={lang.data.overview}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(printparam.grayscale)}
                    onChange={_changeCheck}
                    color="primary"
                    name="grayscale"
                  />
                }
                label={lang.data.grayscale}
              />
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} className={classes.actionDiv}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.actionBtn}
            onClick={() => {
              setloading(true);
              setdpi(32);
              setpreview(true);
            }}
            disabled={Boolean(loading)}
          >
            {lang.data.preview}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.actionBtn}
            onClick={genPdf}
            disabled={Boolean(loading)}
          >
            {lang.data.print}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default PrintDesign;
